import React, { memo, useRef, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

function isOverflown(element: Element): boolean {
	return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

interface Props {
	width: number;
	tooltipWidth?: number;
	value: string | JSX.Element;
	tooltipValue: string | undefined;
	tooltipOnHover: 'always' | 'ifOverflow';
	cellOverflow: 'visible' | 'ellipsis';
}

const GridCellWithTooltip = memo((props: Props) => {
	const classes = useStyles();
	const {
		width,
		value,
		tooltipValue = props.value,
		tooltipOnHover,
		cellOverflow,
		tooltipWidth = width,
	} = props;
	const wrapper = useRef<HTMLDivElement | null>(null);
	const cellDiv = useRef(null);
	const cellValue = useRef(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [showFullCell, setShowFullCell] = useState(false);
	const [showPopper, setShowPopper] = useState(false);

	const handleMouseEnter = () => {
		const showTooltip =
			tooltipValue === undefined
				? false
				: tooltipOnHover === 'ifOverflow'
				? isOverflown(cellValue.current!)
				: tooltipOnHover === 'always';
		setShowPopper(showTooltip);
		setAnchorEl(cellDiv.current);
		setShowFullCell(true);
	};

	const handleMouseLeave = () => {
		setShowFullCell(false);
	};

	useEffect(() => {
		if (!showFullCell) {
			return undefined;
		}

		function handleKeyDown(nativeEvent: KeyboardEvent) {
			// IE11, Edge (prior to using Bink?) use 'Esc'
			if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
				setShowFullCell(false);
			}
		}

		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [setShowFullCell, showFullCell]);

	return (
		<Box
			ref={wrapper}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			sx={{
				alignItems: 'center',
				lineHeight: '24px',
				width: 1,
				height: 1,
				position: 'relative',
				display: 'flex',
			}}
		>
			<Box
				ref={cellDiv}
				sx={{
					height: 1,
					width,
					display: 'block',
					position: 'absolute',
					top: 0,
					cursor: 'pointer',
				}}
			/>
			<Box
				ref={cellValue}
				sx={{
					whiteSpace: 'nowrap',
					overflow: cellOverflow === 'ellipsis' ? 'hidden' : 'visible',
					textOverflow: 'ellipsis',
				}}
			>
				{value}
			</Box>
			{showPopper && (
				<Popper
					open={showFullCell && anchorEl !== null}
					anchorEl={anchorEl}
					style={{ width: tooltipWidth }}
				>
					<Paper
						elevation={1}
						className={classes.tooltip}
						sx={{
							minHeight: wrapper.current!.offsetHeight - 3,
						}}
					>
						<Typography variant="body2" p={1}>
							{tooltipValue}
						</Typography>
					</Paper>
				</Popper>
			)}
		</Box>
	);
});

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tooltip: {
			background: theme.palette.common.black,
			color: theme.palette.common.white,
		},
	}),
);

export default GridCellWithTooltip;
